import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useTranslate } from '@tolgee/react'
import dayjs from 'dayjs'
import {
  getColumnFilterInfo,
  MRT_Column,
  MRT_ColumnDef,
  MRT_FilterOptionMenu,
  MRT_Header,
  MRT_RowData,
  MRT_TableInstance,
} from 'material-react-table'
import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react'
import { formatDate } from './tom-table.utils'

const parseFromValuesOrFunc = <T, U>(fn: ((arg: U) => T) | T | undefined, arg: U): T | undefined =>
  fn instanceof Function ? fn(arg) : fn

const allowNumericValuesOnly = (e: KeyboardEvent<HTMLDivElement>) => {
  if (
    [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ].includes(e.key)
  )
    return

  if (!/[0-9]/.test(e.key)) e.preventDefault()
}

type DateFilterProps<T extends MRT_RowData> = {
  column: MRT_Column<T, unknown>
  header: MRT_Header<T>
  rangeFilterIndex?: number
  table: MRT_TableInstance<T>
}

type DateFilterValue = {
  start?: unknown
  end?: unknown
}

const DateFilter = <T extends MRT_RowData>({
  column,
  header,
  rangeFilterIndex,
  table,
}: DateFilterProps<T>) => {
  const { t } = useTranslate()
  const {
    options: {
      icons: { FilterListIcon },
      localization,
      muiFilterDatePickerProps,
      muiFilterTextFieldProps,
    },
  } = table

  const { currentFilterOption } = getColumnFilterInfo({ header, table })

  const { columnDef } = column
  const args = { column, rangeFilterIndex, table }

  const textFieldProps = {
    ...parseFromValuesOrFunc(muiFilterTextFieldProps, args),
    ...parseFromValuesOrFunc(columnDef.muiFilterTextFieldProps, args),
  }

  const datePickerProps = {
    ...parseFromValuesOrFunc(muiFilterDatePickerProps, args),
    ...parseFromValuesOrFunc(columnDef.muiFilterDatePickerProps, args),
  } as any

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const additionalTextFieldProps = {
    helperText: (
      <label>
        {localization.filterMode.replace(
          '{filterType}',
          localization[
            `filter${
              currentFilterOption?.charAt(0)?.toUpperCase() + currentFilterOption?.slice(1)
            }` as keyof typeof localization
          ],
        )}
      </label>
    ),
    slotProps: {
      formHelperText: {
        sx: {
          fontSize: '0.75rem',
          lineHeight: '0.8rem',
          whiteSpace: 'nowrap',
        },
      },
    },
    InputProps: {
      startAdornment: (
        <InputAdornment position='start'>
          <Tooltip title={localization.changeFilterMode}>
            <span>
              <IconButton
                aria-label={localization.changeFilterMode}
                onClick={(event: MouseEvent<HTMLElement>) => {
                  setAnchorEl(event.currentTarget)
                }}
                size='small'
                sx={{ height: '1.75rem', width: '1.75rem' }}
              >
                <FilterListIcon />
              </IconButton>
            </span>
          </Tooltip>
        </InputAdornment>
      ),
    },
  }

  const slotProps = {
    field: {
      clearable: true,
      onClear: () => {
        setStartDate(null)
        setEndDate(null)
        header.column.setFilterValue(undefined)
      },
      ...datePickerProps?.slotProps?.field,
    },
    textField: {
      fullWidth: true,
      variant: 'standard',
      ...datePickerProps?.slotProps?.textField,
      ...textFieldProps,
    },
  }

  const isBetweenFilter = currentFilterOption === 'customDateBetweenFn'
  const isEmptyFilter = currentFilterOption === 'empty'

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const columnFilterValue = header.column.getFilterValue() as DateFilterValue | undefined

  const parseDateValue = (value: unknown): dayjs.Dayjs | null => {
    if (!value) {
      return null
    }

    const parsedDate = dayjs(value as string | Date)
    return parsedDate.isValid() ? parsedDate : null
  }

  const isSameDate = (first: dayjs.Dayjs | null, second: dayjs.Dayjs | null) =>
    first === second || (!!first && !!second && first.isSame(second))

  useEffect(() => {
    if (currentFilterOption === 'empty') {
      setStartDate(null)
      setEndDate(null)
    }
  }, [currentFilterOption])

  useEffect(() => {
    if (isEmptyFilter) {
      return
    }

    const nextStartDate = parseDateValue(columnFilterValue?.start)
    const nextEndDate = parseDateValue(columnFilterValue?.end)

    setStartDate(previousValue =>
      isSameDate(previousValue, nextStartDate) ? previousValue : nextStartDate,
    )
    setEndDate(previousValue =>
      isSameDate(previousValue, nextEndDate) ? previousValue : nextEndDate,
    )
  }, [columnFilterValue, isEmptyFilter])

  const createFilterValue = (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => {
    const isBetween = currentFilterOption === 'customDateBetweenFn'

    return {
      start,
      end,
      toString: function () {
        if (isBetween) {
          if (!this.start && !this.end) return ''
          const startDateText = this.start ? formatDate(this.start) : '...'
          const endDateText = this.end ? formatDate(this.end) : '...'
          return `${startDateText} - ${endDateText}`
        }
        return this.start ? formatDate(this.start) : ''
      },
    }
  }

  return (
    <>
      <Stack sx={{ gap: '0.5rem' }} direction='row'>
        {isEmptyFilter && (
          <Autocomplete
            clearIcon={false}
            options={[]}
            freeSolo
            renderValue={value => (
              <Chip
                label={value}
                onDelete={() => {
                  setStartDate(null)
                  setEndDate(null)
                  header.column.setFilterValue(undefined)
                  header.column.filterFn = 'customDateEqualFn'
                  // table.setColumnFilters(old => {
                  //   return [
                  //     ...old.filter(f => f.id !== header.column.id),
                  //     { id: header.column.id, value: '' },
                  //   ]
                  // })
                  table.setColumnFilterFns(old => {
                    return { ...old, [header.column.id]: 'customDateEqualFn' }
                  })
                }}
              />
            )}
            value={t('empty', 'Empty')}
            renderInput={params => (
              <TextField
                {...muiFilterTextFieldProps}
                {...additionalTextFieldProps}
                slotProps={{
                  ...additionalTextFieldProps.slotProps,
                  htmlInput: { ...params.inputProps },
                  input: {
                    ...params.InputProps,
                    inputProps: {
                      ...params.inputProps,
                    },
                    startAdornment: (
                      <>
                        {additionalTextFieldProps.InputProps.startAdornment}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        )}
        {!isEmptyFilter && (
          <Box sx={{ width: '220px' }}>
            <DatePicker
              {...muiFilterDatePickerProps}
              value={startDate}
              onChange={date => {
                setStartDate(date)
                header.column.setFilterValue(
                  date?.isValid() ? createFilterValue(date, endDate) : undefined,
                )
              }}
              slotProps={{
                ...slotProps,
                textField: { ...slotProps.textField, ...additionalTextFieldProps },
              }}
            />
          </Box>
        )}
        {isBetweenFilter && (
          <Box sx={{ width: '220px' }}>
            <DatePicker
              value={endDate}
              {...muiFilterDatePickerProps}
              onChange={date => {
                setEndDate(date)
                header.column.setFilterValue(
                  date?.isValid() ? createFilterValue(startDate, date) : undefined,
                )
              }}
              slotProps={slotProps}
            />
          </Box>
        )}
      </Stack>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <MRT_FilterOptionMenu
        anchorEl={anchorEl}
        header={header}
        setAnchorEl={setAnchorEl}
        table={table}
      />
    </>
  )
}

export const tomTableColumnFilterPresets = {
  searchOptional: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'text',
    enableColumnFilterModes: true,
    columnFilterModeOptions: ['contains', 'equals', 'empty', 'notEmpty'],
    filterFn: 'contains',
  }),
  searchMandatory: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'text',
    enableColumnFilterModes: true,
    columnFilterModeOptions: ['contains', 'equals'],
    filterFn: 'contains',
  }),
  numericOptional: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'text',
    muiFilterTextFieldProps: { onKeyDown: allowNumericValuesOnly },
    enableColumnFilterModes: true,
    columnFilterModeOptions: [
      'contains',
      'equals',
      'empty',
      'notEmpty',
      'greaterThan',
      'greaterThanOrEqualTo',
      'lessThan',
      'lessThanOrEqualTo',
    ],
    filterFn: 'contains',
  }),
  numericMandatory: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'text',
    muiFilterTextFieldProps: {
      onKeyDown: allowNumericValuesOnly,
    },
    enableColumnFilterModes: true,
    columnFilterModeOptions: [
      'contains',
      'equals',
      'greaterThan',
      'greaterThanOrEqualTo',
      'lessThan',
      'lessThanOrEqualTo',
    ],
    filterFn: 'contains',
  }),
  dateMandatory: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    enableColumnFilterModes: true,
    Filter: DateFilter<T>,
    renderColumnFilterModeMenuItems: ({ onSelectFilterMode }) => [
      <MenuItem key='customDateBetweenFn' onClick={() => onSelectFilterMode('customDateBetweenFn')}>
        <ListItemIcon>↔</ListItemIcon>
        <ListItemText>Between</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateEqualFn' onClick={() => onSelectFilterMode('customDateEqualFn')}>
        <ListItemIcon>=</ListItemIcon>
        <ListItemText>Equals</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateAfterFn' onClick={() => onSelectFilterMode('customDateAfterFn')}>
        <ListItemIcon>≥</ListItemIcon>
        <ListItemText>From</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateBeforeFn' onClick={() => onSelectFilterMode('customDateBeforeFn')}>
        <ListItemIcon>≤</ListItemIcon>
        <ListItemText>Before</ListItemText>
      </MenuItem>,
    ],
    filterFn: 'customDateEqualFn',
  }),
  dateOptional: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    enableColumnFilterModes: true,
    Filter: DateFilter<T>,
    renderColumnFilterModeMenuItems: ({ onSelectFilterMode }) => [
      <MenuItem key='customDateBetweenFn' onClick={() => onSelectFilterMode('customDateBetweenFn')}>
        <ListItemIcon>↔</ListItemIcon>
        <ListItemText>Between</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateEqualFn' onClick={() => onSelectFilterMode('customDateEqualFn')}>
        <ListItemIcon>=</ListItemIcon>
        <ListItemText>Equals</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateAfterFn' onClick={() => onSelectFilterMode('customDateAfterFn')}>
        <ListItemIcon>≥</ListItemIcon>
        <ListItemText>From</ListItemText>
      </MenuItem>,
      <MenuItem key='customDateBeforeFn' onClick={() => onSelectFilterMode('customDateBeforeFn')}>
        <ListItemIcon>≤</ListItemIcon>
        <ListItemText>Before</ListItemText>
      </MenuItem>,
      <MenuItem key='empty' onClick={() => onSelectFilterMode('empty')}>
        <ListItemIcon>(∅)</ListItemIcon>
        <ListItemText>Empty</ListItemText>
      </MenuItem>,
      <MenuItem key='empty' onClick={() => onSelectFilterMode('notEmpty')}>
        <ListItemIcon>(!∅)</ListItemIcon>
        <ListItemText>Not Empty</ListItemText>
      </MenuItem>,
    ],
    filterFn: 'customDateEqualFn',
  }),
  yesNoSelect: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'select',
    filterSelectOptions: ['Yes', 'No'],
    filterFn: 'equals',
    enableColumnFilterModes: false,
  }),
  arrayContainsValue: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'select',
    filterSelectOptions: ['Yes', 'No'],
    filterFn: (row, columnId, filterValue) => {
      const value = row.getValue<string[] | string>(columnId)
      const shouldDisplay = value?.length > 0 && value !== 'No' && value !== '-'

      return filterValue === 'Yes' ? shouldDisplay : !shouldDisplay
    },
    enableColumnFilterModes: false,
  }),
  zeroOrMore: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'select',
    filterSelectOptions: ['0', '≥ 1'],
    filterFn: (row, columnId, filterValue) => {
      const value = row.getValue<number>(columnId)
      return filterValue === '0' ? value === 0 : value >= 1
    },
    enableColumnFilterModes: false,
  }),
  fullEmptySelect: <T extends MRT_RowData>(): Partial<MRT_ColumnDef<T, unknown>> => ({
    filterVariant: 'select',
    filterSelectOptions: ['Full', 'Empty'],
    filterFn: 'equals',
    enableColumnFilterModes: false,
  }),
}
